import {
  parseInlineMarkdown,
  serializeInlineMarkdown,
} from "@app/components/editor/document/content";
import { isWritableThread } from "@app/components/editor/document/dfm_persistence";
import type { DocumentCommentDraft } from "@app/components/editor/document/DocumentComments";
import {
  documentCommentsPluginKey,
  getClickedCommentIds,
  getCommentedTexts,
  getCommentInlineContent,
  getCommentStarts,
  getDocumentComments,
  getDraftInlineContent,
  getSuggestableCommentIds,
  scrollToCommentHighlight,
  withoutOrphanCommentMarks,
} from "@app/components/editor/document/DocumentComments";
import { getLiveCommentAnchor } from "@app/components/editor/document/liveCommentAnchor";
import type { ActiveCommentBinding } from "@app/components/editor/document/types";
import type { CommentChannel } from "@app/lib/client/collab/comments";
import type { DfmMessageVerifier } from "@app/lib/client/dfm_signatures";
import type { DfmAuthor, DfmComment, DfmMessage } from "@app/lib/markdown/dfm";
import { suggestionBlock } from "@app/lib/markdown/dfm";
import { concurrentExecutor } from "@app/lib/utils/async_utils";
import type {
  CommentRequest,
  CommentRequestErrorCode,
} from "@app/types/collab";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ChainedCommands, Editor, JSONContent } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { useEditorState } from "@tiptap/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

interface UseDocumentCommentsProps {
  editor: Editor | null;
  /** The document is editable. */
  canComment: boolean;
  author: DfmAuthor | undefined;
  /** Whether the document, as TipTap JSON, would save. */
  isSavable: (document: JSONContent) => boolean;
  verify?: DfmMessageVerifier;
  live?: CommentChannel;
  activeComment?: ActiveCommentBinding;
}

interface Verification {
  /** The verifier the results belong to; results from another are stale. */
  verify: DfmMessageVerifier;
  /** Whether each message verified, by `verificationKey`. */
  results: Map<string, boolean>;
}

interface EditorCommentsState {
  /** Commented text by comment id, in document order. */
  quotes: Map<string, string>;
  /** Position of the first commented text by comment id. */
  starts: Map<string, number>;
  activeId: string | null;
  draft: DocumentCommentDraft | null;
  /** The text the pending draft covers. */
  draftQuote: string;
  suggestable: Set<string>;
  draftSuggestable: boolean;
}

const EMPTY_COMMENTS: DfmComment[] = [];

const EMPTY_STATE: EditorCommentsState = {
  quotes: new Map(),
  starts: new Map(),
  activeId: null,
  draft: null,
  draftQuote: "",
  suggestable: new Set(),
  draftSuggestable: false,
};

const UNAVAILABLE_MESSAGE = msg`Commenting is unavailable.`;
const UNANCHORED_MESSAGE = msg`The selected text can no longer take a comment. Select other text to comment.`;
const UNSAVABLE_MESSAGE = msg`This comment can't be saved in this document. Try shortening or simplifying it.`;
const DOCUMENT_UNSAVABLE_MESSAGE = msg`This document can't be saved right now.`;

const jumpToComment = (editor: Editor, id: string) => {
  editor.commands.setActiveComment(id);
  scrollToCommentHighlight(editor, id);
};

/** The draft's text, shown as its quote and sent as the one agents get. */
const getDraftQuote = (doc: Node, draft: DocumentCommentDraft) =>
  doc.textBetween(draft.from, draft.to, " ");

const UNSUGGESTED_MESSAGE = msg`This suggestion can't be saved in this document.`;
const UNSUGGESTABLE_MESSAGE = msg`Suggestions can only replace text within one paragraph.`;
const UNAPPLICABLE_MESSAGE = msg`This suggestion can't replace the commented text. Another comment may cover part of it.`;
const DELETED_MESSAGE = msg`This comment was deleted.`;
const THREAD_CHANGED_MESSAGE = msg`This thread changed while sending. Send again.`;

const ED25519_BASE64URL_SIGNATURE_PLACEHOLDER = "A".repeat(86);

const withMessage = (
  commentId: string,
  thread: DfmComment | undefined,
  added: DfmMessage
): DfmComment =>
  thread
    ? { ...thread, messages: [...thread.messages, added] }
    : { id: commentId, status: "open", messages: [added] };

const localMessage = (writer: DfmAuthor, body: string): DfmMessage => ({
  author: writer,
  createdAt: new Date().toISOString(),
  body,
});

/** What a message's signature covers besides the file: its thread, place and previous message. */
const verificationKey = (comment: DfmComment, index: number) =>
  JSON.stringify([
    comment.id,
    index,
    comment.messages[index - 1] ?? null,
    comment.messages[index],
  ]);

/** Comment changes, sent to the live session. */
interface CommentCommands {
  add: (id: string, body: string) => Promise<Result<DfmComment, string>>;
  reply: (thread: DfmComment, body: string) => Promise<Result<void, string>>;
  setResolved: (id: string, resolved: boolean) => Promise<Result<void, string>>;
  remove: (id: string) => Promise<Result<void, string>>;
  /** Drops a thread `add` created that the draft could no longer anchor. */
  discard: (id: string) => void;
}

/** The document the commands would produce, or null when one of them refuses. */
const previewDocument = (
  editor: Editor,
  apply: (chain: ChainedCommands) => ChainedCommands
): Node | null => {
  let next: Node | null = null;
  const applied = apply(editor.chain())
    .command(({ tr }) => {
      next = tr.doc;
      // TipTap drops a chain's transaction carrying this meta instead of dispatching it.
      tr.setMeta("preventDispatch", true);
      return true;
    })
    .run();
  return applied ? next : null;
};

interface ThreadFailure {
  /** The thread as it was when refused; the refusal no longer applies once it changes. */
  comment: DfmComment;
  message: string;
}

/** Where the comments card or list should move focus once it has rendered. */
export interface CommentsFocusRequest {
  /** Thread to focus, or null for the list heading. */
  threadId: string | null;
  nonce: number;
}

/**
 * @cc [owner:flvndvd;tdraier,label:product] document-comment-authoring
 * Starting, submitting, replying to, resolving and deleting comments MUST require canComment,
 * an author and a live session. A pending draft MUST be cancelled when commenting becomes unavailable. New
 * comments and replies MUST carry the current author and creation time, and MUST be refused
 * with a reason, leaving the document unchanged, when the codec cannot write the thread or the
 * document would no longer save with it.
 */
/**
 * @cc [owner:flvndvd;tdraier,label:react] document-comment-navigation
 * Selecting a thread MUST make it active and scroll its highlight into view. Revealing a
 * comment from a highlight or marker MUST close the comments list, make the comment active so its
 * card floats under its text, and request focus on that thread. Opening the list from its toggle
 * MUST cancel a pending draft and request focus on the list. Starting a draft MUST close the list.
 * Closing the list while focus is inside it MUST return focus to the toggle. While a draft is
 * pending, revealing MUST NOT change the active comment, so the draft's card and its typed text
 * stay.
 */
/**
 * @cc [owner:tdraier,label:react] document-thread-actions
 * While a thread's resolve, reopen, delete or suggestion is pending, another of them on that
 * thread MUST be ignored, wherever it starts and even after the list or card that started it
 * closed. A refused one MUST read on the thread, with its reason, until the thread changes.
 */
/**
 * @cc [owner:tdraier,label:security] document-comment-verification
 * With a verifier, each message MUST read as verified or unverified from the current verifier's
 * answer for that message at its place in its thread after the same previous message, and as
 * unknown while that answer is pending. Without a verifier, every message MUST read as unknown,
 * never as verified.
 */
/**
 * @cc [owner:tdraier,label:product] document-comment-suggestion
 * Applying a suggestion MUST require canWrite and an open thread, MUST replace the commented text
 * with the suggestion's content, and only once that text change is in the document MUST it resolve
 * the thread outside text undo history; when that resolution is refused, the text change MUST stay
 * and the refusal MUST be returned with its reason. It MUST be refused with a reason, leaving the
 * document unchanged, when the text change is refused or filtered out, when the suggestion is not
 * one paragraph of inline Markdown, the commented text spans more than one textblock, or the
 * document would no longer save. A suggestion template MUST hold the current commented text as
 * Markdown, so applying it unchanged leaves the text as it is.
 */
/**
 * @cc [owner:tdraier,label:product] collab-editor-comment-requests
 * With `live`, posting, replying to, resolving and deleting comments MUST go through the session
 * and MUST NOT change the threads in the document, which only the session sends, with two
 * exceptions: a pending draft MUST end on the new thread the session accepted, and deleting MUST
 * remove the comment's marks once the session deleted the thread or answered that it has none,
 * never before. A new thread MUST be sent with the draft's anchor (`live-comment-anchor`), which
 * the session marks, and MUST NOT be marked by the editor; without an anchor it MUST be refused
 * before the session sees it. A new thread MUST be deleted from the session when the session
 * accepted it without returning it or returned a thread the codec cannot write. A new comment or
 * reply the codec could not write, or the document would no longer save with, MUST be refused
 * before the session sees it, checked with the current author and a
 * full-length signature, and without the comment marks that have no thread, which the session drops
 * when it writes the file. Every refusal from the session, and a command that could not be sent,
 * MUST be returned with a reason, never thrown; applying a suggestion whose resolution the session
 * refuses MUST keep the text change and return that reason.
 */
/**
 * @cc [owner:adrien,label:react] document-comment-reveal-on-open
 * When `activeComment.id` names a loaded thread whose text is in the document and that differs
 * from the last thread the hook opened or reported, the hook MUST activate it and scroll its
 * highlight into view, without moving focus, even if an earlier request asked to focus it. While a
 * draft is pending, or for a thread not loaded or not in the document, it MUST change nothing.
 */
/**
 * @cc [owner:adrien,label:react] document-comment-report-active
 * Each time the active thread changes, including to none, other than by opening `activeComment.id`,
 * the hook MUST call `activeComment.onChange` with the new id once; it MUST NOT call it on mount. A
 * change made without `activeComment` MUST be reported once it is given again.
 */
export const useDocumentComments = ({
  editor,
  canComment,
  author,
  isSavable,
  verify,
  live,
  activeComment,
}: UseDocumentCommentsProps) => {
  const { t } = useLingui();
  const state =
    useEditorState({
      editor,
      selector: ({ editor }): EditorCommentsState => {
        if (!editor) {
          return EMPTY_STATE;
        }
        const pluginState = documentCommentsPluginKey.getState(editor.state);
        const draft = pluginState?.draft ?? null;
        return {
          quotes: getCommentedTexts(editor.state.doc),
          starts: getCommentStarts(editor.state.doc),
          activeId: pluginState?.activeId ?? null,
          draft,
          draftQuote: draft ? getDraftQuote(editor.state.doc, draft) : "",
          suggestable: getSuggestableCommentIds(editor.state.doc),
          draftSuggestable: draft
            ? editor.state.doc
                .resolve(draft.from)
                .sameParent(editor.state.doc.resolve(draft.to))
            : false,
        };
      },
    }) ?? EMPTY_STATE;
  // Selected apart so the threads keep their identity while only the selection or draft moves.
  const comments =
    useEditorState({
      editor,
      selector: ({ editor }): DfmComment[] =>
        editor ? getDocumentComments(editor.state.doc) : EMPTY_COMMENTS,
    }) ?? EMPTY_COMMENTS;
  const [listOpen, setListOpen] = useState(false);
  const [focusRequest, setFocusRequest] = useState<CommentsFocusRequest | null>(
    null
  );
  const [verification, setVerification] = useState<Verification | null>(null);
  const pendingThreadIds = useRef(new Set<string>());
  const [busyThreadIds, setBusyThreadIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [threadFailures, setThreadFailures] = useState<
    ReadonlyMap<string, ThreadFailure>
  >(() => new Map());
  const latestComments = useRef(comments);
  const latestVerification = useRef<Verification | null>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLElement>(null);
  const canWrite = canComment && author !== undefined && live !== undefined;
  // Stable identity matters: the markers re-measure the DOM whenever this array changes.
  const unresolved = useMemo(
    () => comments.filter((comment) => comment.status === "open"),
    [comments]
  );

  // Signature checks are asynchronous WebCrypto calls; results are kept by what each signature
  // covers, so new threads only wait for the messages they changed.
  const verifiedByPosition = useMemo(() => {
    const results = new Map<string, boolean>();
    if (!verify || verification?.verify !== verify) {
      return results;
    }
    for (const comment of comments) {
      comment.messages.forEach((_, index) => {
        const valid = verification.results.get(verificationKey(comment, index));
        if (valid !== undefined) {
          results.set(`${comment.id}:${index}`, valid);
        }
      });
    }
    return results;
  }, [comments, verification, verify]);

  useEffect(() => {
    if (!verify) {
      return;
    }
    let cancelled = false;
    const known =
      latestVerification.current?.verify === verify
        ? latestVerification.current.results
        : null;
    void concurrentExecutor(
      comments.flatMap((comment) =>
        comment.messages.map((_, index) => ({ comment, index }))
      ),
      async ({ comment, index }) => {
        const key = verificationKey(comment, index);
        return [
          key,
          known?.get(key) ??
            (await verify(comment.id, comment.messages, index)),
        ] as const;
      },
      { concurrency: 8 }
    ).then((entries) => {
      if (!cancelled) {
        latestVerification.current = { verify, results: new Map(entries) };
        setVerification(latestVerification.current);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [comments, verify]);

  useEffect(() => {
    latestComments.current = comments;
  }, [comments]);

  useEffect(() => {
    if (!canWrite && state.draft && editor) {
      editor.commands.cancelCommentDraft();
    }
  }, [canWrite, state.draft, editor]);

  const requestFocus = (threadId: string | null) =>
    setFocusRequest((current) => ({
      threadId,
      nonce: (current?.nonce ?? 0) + 1,
    }));

  const select = (id: string | null) => {
    editor?.commands.setActiveComment(id);
  };

  const reveal = (id: string) => {
    if (state.draft) {
      return;
    }
    select(id);
    setListOpen(false);
    requestFocus(id);
  };

  const linkedCommentId = activeComment?.id;
  const onLinkedCommentChange = activeComment?.onChange;
  const syncedCommentId = useRef(state.activeId);
  useEffect(() => {
    if (!editor || !onLinkedCommentChange) {
      return;
    }
    if (state.activeId !== syncedCommentId.current) {
      syncedCommentId.current = state.activeId;
      onLinkedCommentChange(state.activeId);
      return;
    }
    if (
      linkedCommentId &&
      linkedCommentId !== state.activeId &&
      !state.draft &&
      state.quotes.has(linkedCommentId) &&
      comments.some(({ id }) => id === linkedCommentId)
    ) {
      syncedCommentId.current = linkedCommentId;
      setFocusRequest(null);
      jumpToComment(editor, linkedCommentId);
    }
  }, [
    editor,
    linkedCommentId,
    onLinkedCommentChange,
    state.activeId,
    state.draft,
    state.quotes,
    comments,
  ]);

  const liveErrorMessage = (code: CommentRequestErrorCode): string => {
    switch (code) {
      case "unavailable":
        return t(UNAVAILABLE_MESSAGE);
      case "not_found":
        return t(DELETED_MESSAGE);
      case "thread_changed":
        return t(THREAD_CHANGED_MESSAGE);
      case "unwritable":
        return t(UNSAVABLE_MESSAGE);
      case "unanchored":
        return t(UNANCHORED_MESSAGE);
      default:
        assertNeverAndIgnore(code);
        return t(UNAVAILABLE_MESSAGE);
    }
  };

  // A live document keeps marks whose thread is gone; the session drops them when it saves.
  const wouldSave = (document: Node): boolean =>
    isSavable(
      withoutOrphanCommentMarks(
        document.toJSON(),
        new Set(getDocumentComments(document).map(({ id }) => id))
      )
    );

  // A document that already fails to save refuses every change, through no fault of the change.
  const unsavableMessage = (editor: Editor, message: MessageDescriptor) =>
    t(wouldSave(editor.state.doc) ? message : DOCUMENT_UNSAVABLE_MESSAGE);

  /**
   * Refuses a new message to `thread`, or a new thread `commentId`, before anyone writes it: when
   * the codec could not write it, or the document would no longer save with it. A message the
   * server writes is checked with a full-length signature.
   */
  const checkNewMessage = (
    editor: Editor,
    writer: DfmAuthor,
    commentId: string,
    thread: DfmComment | undefined,
    body: string
  ): Result<void, string> => {
    const message = {
      ...localMessage(writer, body),
      signature: ED25519_BASE64URL_SIGNATURE_PLACEHOLDER,
    };
    const added = withMessage(commentId, thread, message);
    if (!isWritableThread(added)) {
      return new Err(t(UNSAVABLE_MESSAGE));
    }
    const next = previewDocument(editor, (chain) =>
      thread
        ? chain.replyToComment(commentId, message)
        : chain.addComment(added)
    );
    if (!next) {
      return new Err(t(thread ? DELETED_MESSAGE : UNANCHORED_MESSAGE));
    }
    return wouldSave(next)
      ? new Ok(undefined)
      : new Err(unsavableMessage(editor, UNSAVABLE_MESSAGE));
  };

  const sessionCommands = (
    editor: Editor,
    channel: CommentChannel
  ): CommentCommands => {
    const sendForReason = async (
      command: CommentRequest
    ): Promise<Result<void, string>> => {
      const sent = await channel.send(command);
      return sent.isOk()
        ? new Ok(undefined)
        : new Err(liveErrorMessage(sent.error));
    };
    // TODO(co-edition): a connection lost while the session stores an `add`, or before it hears
    // this delete, closes the channel, so the thread stays in the session without an anchor.
    const discard = (id: string) => {
      void channel.send({ type: "delete", commentId: id });
    };
    return {
      add: async (id, body) => {
        const draft = documentCommentsPluginKey.getState(editor.state)?.draft;
        const anchor = draft ? getLiveCommentAnchor(editor.state, draft) : null;
        if (!draft || !anchor) {
          return new Err(t(UNANCHORED_MESSAGE));
        }
        const created = await channel.send({
          type: "add",
          commentId: id,
          body,
          quote: getDraftQuote(editor.state.doc, draft),
          anchor,
        });
        if (created.isErr()) {
          return new Err(liveErrorMessage(created.error));
        }
        if (!created.value) {
          discard(id);
          return new Err(t(UNAVAILABLE_MESSAGE));
        }
        if (!isWritableThread(created.value)) {
          discard(id);
          return new Err(t(UNSAVABLE_MESSAGE));
        }
        return new Ok(created.value);
      },
      // The session refuses the reply once the thread has moved past what was shown.
      reply: (thread, body) =>
        sendForReason({
          type: "reply",
          commentId: thread.id,
          position: thread.messages.length,
          body,
        }),
      setResolved: (id, resolved) =>
        sendForReason({ type: "resolve", commentId: id, resolved }),
      // A thread already gone is what a delete asks for.
      remove: async (id) => {
        const deleted = await channel.send({
          type: "delete",
          commentId: id,
        });
        if (deleted.isErr() && deleted.error !== "not_found") {
          return new Err(liveErrorMessage(deleted.error));
        }
        editor.commands.removeCommentMarks(id);
        return new Ok(undefined);
      },
      discard,
    };
  };

  const suggestionTemplate = (
    content: JSONContent[] | null
  ): Result<string, string> => {
    if (!content) {
      return new Err(t(UNSUGGESTABLE_MESSAGE));
    }
    const markdown = serializeInlineMarkdown(content);
    if (markdown.isErr()) {
      return markdown;
    }
    const block = suggestionBlock(markdown.value);
    return block.isOk()
      ? block
      : new Err(t`This text is too long or complex to suggest a change to.`);
  };

  const recordThreadResult = (id: string, done: Result<void, string>) =>
    setThreadFailures((current) => {
      const next = new Map(current);
      // Keyed to the thread as it is now: a suggestion changes the document before it is refused.
      const comment = latestComments.current.find((thread) => thread.id === id);
      if (done.isErr() && comment) {
        next.set(id, { comment, message: done.error });
      } else {
        next.delete(id);
      }
      return next;
    });

  return {
    /** Whether a message's signature checked out, or null while unknown. */
    isVerified: (commentId: string, index: number): boolean | null =>
      verifiedByPosition.get(`${commentId}:${index}`) ?? null,
    comments,
    unresolved,
    quotes: state.quotes,
    starts: state.starts,
    activeId: state.activeId,
    draft: canWrite ? state.draft : null,
    draftQuote: state.draftQuote,
    canWrite,
    author,
    listOpen,
    /** Threads with a resolve, reopen, delete or suggestion pending. */
    busyThreadIds,
    /** The reason the thread's last action was refused, until the thread changes. */
    threadError: (comment: DfmComment): string | null => {
      const failure = threadFailures.get(comment.id);
      return failure?.comment === comment ? failure.message : null;
    },
    /**
     * Runs a resolve, reopen, delete or suggestion on a thread, unless one is pending on it.
     * Resolves to null for an ignored action.
     */
    runThreadAction: async (
      id: string,
      action: () => Promise<Result<void, string>>
    ): Promise<Result<void, string> | null> => {
      if (pendingThreadIds.current.has(id)) {
        return null;
      }
      pendingThreadIds.current.add(id);
      setBusyThreadIds(new Set(pendingThreadIds.current));
      try {
        const done = await action();
        recordThreadResult(id, done);
        return done;
      } finally {
        pendingThreadIds.current.delete(id);
        setBusyThreadIds(new Set(pendingThreadIds.current));
      }
    },
    focusRequest,
    toggleRef,
    listRef,
    select,
    closeList: () => {
      if (listRef.current?.contains(document.activeElement)) {
        toggleRef.current?.focus();
      }
      setListOpen(false);
    },
    toggleList: () => {
      if (!listOpen) {
        if (state.draft) {
          editor?.commands.cancelCommentDraft();
        }
        requestFocus(null);
      }
      setListOpen((open) => !open);
    },
    /** Floats a comment's card under its text, from a highlight or marker. */
    reveal,
    /** Clears the active comment, closing its card, and hands focus back to the text. */
    closeThread: () => {
      select(null);
      editor?.commands.focus();
    },
    /**
     * Reveals the comment under a click in the text: the one covering the least text first,
     * then wider ones on repeated clicks. A click outside every comment clears the active one.
     */
    revealClicked: (target: EventTarget | null) => {
      // A drag or double click on commented text selects it; revealing would steal its focus.
      if (!editor || !editor.state.selection.empty) {
        return;
      }
      const clicked = getClickedCommentIds(target, editor.view.dom);
      if (clicked.length === 0) {
        if (state.activeId !== null) {
          select(null);
        }
        return;
      }
      const ids = clicked.sort(
        (a, b) =>
          (state.quotes.get(a)?.length ?? 0) -
          (state.quotes.get(b)?.length ?? 0)
      );
      const current = state.activeId ? ids.indexOf(state.activeId) : -1;
      reveal(ids[(current + 1) % ids.length]);
    },
    /** Activates a thread from the list and scrolls to its text. */
    jumpTo: (id: string) => {
      if (editor) {
        jumpToComment(editor, id);
      }
    },
    /** Starts a comment on the selection, in a card floating under it. */
    startDraft: () => {
      if (!canWrite || !editor || !editor.commands.startCommentDraft()) {
        return false;
      }
      // The draft card takes focus; an older request would steal it.
      setFocusRequest(null);
      setListOpen(false);
      return true;
    },
    cancelDraft: () => {
      editor?.chain().cancelCommentDraft().focus().run();
    },
    /** Cancels the draft and leaves focus where it is, for a press elsewhere. */
    dismissDraft: () => {
      editor?.commands.cancelCommentDraft();
    },
    submitDraft: async (body: string): Promise<Result<void, string>> => {
      if (!canWrite || !editor || !author || !live || !state.draft) {
        return new Err(t(UNAVAILABLE_MESSAGE));
      }
      const id = uuidv4();
      const checked = checkNewMessage(editor, author, id, undefined, body);
      if (checked.isErr()) {
        return checked;
      }
      const created = await sessionCommands(editor, live).add(id, body);
      if (created.isErr()) {
        return created;
      }
      // The session marked the text before accepting; the draft may have been cancelled since.
      const draft = documentCommentsPluginKey.getState(editor.state)?.draft;
      if (draft) {
        editor
          .chain()
          .commitCommentDraft(created.value)
          .focus()
          .setTextSelection(draft.to)
          .run();
      }
      return new Ok(undefined);
    },
    reply: async (id: string, body: string): Promise<Result<void, string>> => {
      const thread = comments.find((comment) => comment.id === id);
      if (!canWrite || !editor || !author || !live || !thread) {
        return new Err(t(UNAVAILABLE_MESSAGE));
      }
      const checked = checkNewMessage(editor, author, id, thread, body);
      if (checked.isErr()) {
        return checked;
      }
      return sessionCommands(editor, live).reply(thread, body);
    },
    /** Resolves or reopens, then focuses the given thread or the list heading. */
    setResolved: async (
      id: string,
      resolved: boolean,
      focusNext: string | null
    ): Promise<Result<void, string>> => {
      if (!canWrite || !editor || !live) {
        return new Err(t(UNAVAILABLE_MESSAGE));
      }
      const done = await sessionCommands(editor, live).setResolved(
        id,
        resolved
      );
      if (done.isOk()) {
        requestFocus(focusNext);
      }
      return done;
    },
    suggestable: state.suggestable,
    draftSuggestable: canWrite && state.draftSuggestable,
    /** A suggestion block holding the commented text, for the field to edit. */
    suggestionTemplate: (id: string): Result<string, string> =>
      editor
        ? suggestionTemplate(getCommentInlineContent(editor.state.doc, id))
        : new Err(t(UNAVAILABLE_MESSAGE)),
    draftSuggestionTemplate: (): Result<string, string> => {
      const draft = editor
        ? documentCommentsPluginKey.getState(editor.state)?.draft
        : null;
      return editor && draft
        ? suggestionTemplate(getDraftInlineContent(editor.state.doc, draft))
        : new Err(t(UNAVAILABLE_MESSAGE));
    },
    /** Replaces the commented text with the suggestion, then resolves the thread. */
    applySuggestion: async (
      id: string,
      suggestion: string,
      focusNext: string | null
    ): Promise<Result<void, string>> => {
      const thread = editor
        ? getDocumentComments(editor.state.doc).find(
            (comment) => comment.id === id
          )
        : undefined;
      if (!canWrite || !editor || !live || thread?.status !== "open") {
        return new Err(t(UNAVAILABLE_MESSAGE));
      }
      const content = parseInlineMarkdown(suggestion);
      if (content.isErr()) {
        return content;
      }
      const next = previewDocument(editor, (chain) =>
        chain
          .applyCommentSuggestion(id, content.value)
          .setCommentResolved(id, true)
      );
      if (!next) {
        return new Err(
          getSuggestableCommentIds(editor.state.doc).has(id)
            ? t(UNAPPLICABLE_MESSAGE)
            : t(UNSUGGESTABLE_MESSAGE)
        );
      }
      if (!wouldSave(next)) {
        return new Err(unsavableMessage(editor, UNSUGGESTED_MESSAGE));
      }
      // Two transactions: the text change is undoable, the resolution stays out of history.
      const before = editor.state.doc;
      if (
        !editor.commands.applyCommentSuggestion(id, content.value) ||
        editor.state.doc === before
      ) {
        return new Err(t(UNAPPLICABLE_MESSAGE));
      }
      const resolved = await sessionCommands(editor, live).setResolved(
        id,
        true
      );
      if (resolved.isErr()) {
        const reason = resolved.error;
        return new Err(
          t`The suggestion was applied, but the comment couldn't be resolved. ${reason}`
        );
      }
      requestFocus(focusNext);
      return new Ok(undefined);
    },
    /** Deletes, then focuses the given thread or the list heading. */
    remove: async (
      id: string,
      focusNext: string | null
    ): Promise<Result<void, string>> => {
      if (!canWrite || !editor || !live) {
        return new Err(t(UNAVAILABLE_MESSAGE));
      }
      const done = await sessionCommands(editor, live).remove(id);
      if (done.isOk()) {
        requestFocus(focusNext);
      }
      return done;
    },
  };
};

export type DocumentCommentsController = ReturnType<typeof useDocumentComments>;
